'use client';

import type { WorkspaceDTO, WorkspaceItemDTO } from '@/lib/workspaces/types';
import { useT } from '@/components/i18n-provider';
import { renderItemSummary } from '@/lib/workspaces/item-summary';

/**
 * Sr-only sibling rendered next to the React-Flow viewport when the
 * shell is being browsed by an anonymous visitor. React-Flow itself
 * is JS-only and invisible to crawlers, so we ship a screen-reader-
 * only summary of every item on the canvas. Google + LLM ingestion
 * pick up the canonical content from this block; the React canvas
 * stays the interactive surface for humans.
 *
 * Pure render — derives everything from the same `initialItems` the
 * canvas itself consumes, so the two surfaces can't drift.
 *
 * Order matches `state.zIndex` (top item first) then creation order,
 * mirroring the canvas's stacking semantics.
 */
export function WorkspaceCanvasTwin({
  workspace,
  items,
  ownerUsername,
}: {
  workspace: WorkspaceDTO;
  items: WorkspaceItemDTO[];
  ownerUsername?: string | null;
}) {
  const t = useT();
  const sorted = [...items].sort((a, b) => {
    const az = a.state.zIndex ?? 0;
    const bz = b.state.zIndex ?? 0;
    if (az !== bz) return bz - az;
    return (a.createdAt ? +new Date(a.createdAt) : 0) - (b.createdAt ? +new Date(b.createdAt) : 0);
  });
  return (
    <section className="sr-only" aria-hidden="false">
      <h1>{workspace.name}</h1>
      {ownerUsername && <p>by @{ownerUsername}</p>}
      {sorted.map((item) => (
        <article key={item.id} data-item-id={item.id} data-item-type={item.type}>
          {renderItemSummary(item)}
        </article>
      ))}
      <nav aria-label={t('twin.linksAria')}>
        <a href="/signup">{t('home.cta.signUp')}</a>
        <a href="/login">{t('home.cta.logIn')}</a>
        <a href="/docs">{t('twin.readDocs')}</a>
        <a href="/discover">{t('channelPage.browseApps')}</a>
      </nav>
      {/*
        Sr-only legal links so crawlers and assistive tech can reach
        Terms / Privacy from every anon-visible canvas without
        depending on a visible footer (the workspace UI deliberately
        has no chrome footer to keep the canvas full-bleed).

        The `/docs/` spelling is load-bearing here. Each legal doc also
        answers at a legacy `/doc/<slug>` alias, but only the `/docs/`
        page is enumerated into the sitemap (robutlerSource via
        /sitemap/docs.xml). Since this block exists specifically to give
        crawlers a path to Terms and Privacy, pointing it at the
        unsubmitted duplicate split the only link equity those two pages
        get. See seo-audit-2026-08-22 item 0.9.
      */}
      <nav aria-label={t('twin.legalAria')}>
        <a href="/docs/terms-of-service">{t('twin.termsOfService')}</a>
        <a href="/docs/privacy-policy">{t('twin.privacyPolicy')}</a>
      </nav>
    </section>
  );
}
